<template>
  <div class="border border-gray-700 rounded-xs p-5 pb-10">
    <IODGuardrail
      v-if="isGuardrailShown"
      :disabilityLabel="props.disabilityLabel"
      class="mb-4"
      aria-label="Impact of Disability reminder"
    />
    <div class="px-5 py-3" v-if="impactAreaOptions.length > 1">
      <h2 id="iod-impact-areas-label" class="text-base font-bold leading-5 m-0 mb-1 p-1 uppercase" >Select Impact Areas</h2>
      <SelectionChip
        v-if="impactAreaOptions.length > 1"
        group-labelledby="iod-impact-areas-label"
        :items="impactAreaOptions"
        :model-value="selectedImpactAreas"
        item-key="area_uuid"
        item-label="area_label"
        :show-icon="true"
        @update:model-value="handleImpactAreaClick"
      />
      <hr class="border-gray-400 mx-5 mt-4" />
    </div>
    
    <component :is="statementCopyWrapper" v-bind="statementCopyWrapperProps" class="mt-4">
      <div v-if="isGeneratingStatement" class="flex flex-col justify-center items-center gap-4 min-h-[200px]">
        <ProgressSpinner class="h-8 w-8 stroke-primary-400" strokeWidth="4" />
        <p>{{ loadingStatementMessage }}</p>
      </div>
      
      <GbInlineMessage v-else-if="statementGenerationError" gbTheme="red" class="mb-4">
        <template #icon></template>
        <div class="inline-flex items-center">
          <span class="material-icons-round text-red-300 mr-2 self-start" aria-hidden="true">warning_amber</span>
          <div>
            <p class="font-bold">Error generating statement</p>
            <p class="text-sm leading-5">We're having trouble generating your impact statement. Please check your internet connection and reload the page to try again.</p>
          </div>
        </div>
      </GbInlineMessage>
      
      <WikiTextEditor
        v-else-if="!isStatementEmptyPlaceholder"
        v-model:wikiText="statement"
        :toEmbeddedText="toEmbeddedText"
        :toWikiText="toWikiText"
        :outer-class="'rounded-md pt-3 px-6 pb-3 mb-3 white-mode text-xl'"
        :placeHolder="loadingStatementMessage"
        :reset-value="unEditedStatement"
        edited-text-type="iodstatement"
        :label="editorAriaLabel"
        @editModeUpdated="handleEditModeUpdated"
      >
        <template #bottomRightContent>
          <div v-if="savedFolderError" class="text-md flex items-center text-red-300 mr-1">
            <span class="material-icons-round">warning_amber</span>
            <span>Save failed.</span>
          </div>
          <div class="flex flex-col gap-2">
            <GbButton
              gbTheme="greenPrimary"
              size="small"
              @click="openSaveModal"
            >
            <span class="flex items-center">  
              <span class="material-icons-round text-lg pr-1">folder</span>
              <span>Save to Folder</span>
            </span>
            </GbButton>

            <ContentReviewButton
              v-if="showReviewButton"
              :criteria-set-name="reviewCriteriaSetName"
              :has-existing-results="hasResults"
              @click="handleAutoReviewButtonClick"
            />
          </div>
        </template>
      </WikiTextEditor>
      
      <div v-else class="p-8 text-center">
        <p class="text-xl italic">{{ statement }}</p>
      </div>
    </component>

    <ContentReviewResults
      v-if="hasResults || isReviewing || reviewError"
      title="Impact Statement Review"
      :results="reviewResults"
      :is-loading="isReviewing"
      :error="reviewError"
      :expected-count="impactCriteria.length"
      @close="handleCloseReviewResults"
      @toggle="handleReviewResultsToggle"
    />

    <ContentReviewModal
      :open="showReviewModal"
      :criteria="impactCriteria"
      :criteria-set-name="reviewCriteriaSetName"
      content-label="Impact Statement"
      description-label="impact of disability statement"
      :subjects="props.subjects"
      @update:open="showReviewModal = $event"
      @review="handleReview"
    />

    <SaveToFolderModal
      v-if="showSaveToFolderModal"
      content-type="saved_impact_of_disability"
      content-type-label="Impact of Disability Statement"
      :content-payload="statementPayload"
      :post-save-navigate="true"
      :confirm-save="saveConfirmation"
      @update:visible="saveToFolderModalVisibilityChanged"
      @save="handleSaveToFolder"
    />

    <SaveIODConfirmationModal v-if="isSaveConfirmationMounted" />
    <PremiumActionModal
      v-if="isSaveBlocked"
      v-model:external-open="isSaveBlockedModalOpen"
      :action-permissions="savePermissions"
    />

    <Dialog :open="showConfirmDialog" @update:open="(val) => { if (!val) cancelImpactAreaChange() }">
      <DialogContent class="sm:max-w-md rounded-lg bg-white shadow-xl p-0">
        <DialogHeader class="py-3 px-5 border-b border-gray-300">
          <DialogTitle class="text-xl font-medium text-gray-900">Confirm Impact Area Change</DialogTitle>
        </DialogHeader>
        <div class="pr-10 pl-5 py-5 text-gray-700">
          <p>If you make this selection, your edits will be lost.</p><br/>
          <p>Do you want to navigate away from your edited statement?</p>
        </div>
        <DialogFooter class="px-5 py-4 border-t border-gray-300 gap-3 justify-end">
          <GbButton
            gbTheme="grayOutlineSecondary"
            @click="cancelImpactAreaChange"
          >
            No
          </GbButton>
          <GbButton
            gbTheme="bluePrimary"
            @click="confirmImpactAreaChange"
          >
            Yes
          </GbButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useContentReviewStore } from '@/content-review/content-review.store'
import { useContentReview } from '@/content-review/composables/useContentReview'
import { reviewImpactStatementStream } from '@/content-review/api'
import ContentReviewButton from '@/content-review/components/ContentReviewButton.vue'
import ContentReviewModal from '@/content-review/components/ContentReviewModal.vue'
import ContentReviewResults from '@/content-review/components/ContentReviewResults.vue'
import { useRouter, useRoute } from 'vue-router'
import SelectionChip from '@/components/SelectionChip.vue'
import WikiTextEditor from '@/components/editor/WikiTextEditor.vue'
import ProgressSpinner from 'primevue/progressspinner'
import { generateIODStatements, getTemplateIODStatements } from './api'
import { buildIODStatement, buildTemplateIODStatement } from './statement-builder'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { IMPACT_OF_DISABILITY_EVENT_NAMES } from './event_names'
import { useImpactStatementReviewAnalytics } from './composables/useImpactStatementReviewAnalytics'
import { useImpactCriteriaAttrs } from './composables/useImpactCriteriaAttrs'
import type { ImpactArea, StatementForDisability } from './impact-of-disability_types'
import { composedImpactAreas } from './composed_impact_areas'
import SaveToFolderModal from '@/components/SaveToFolderModal.vue'
import GbButton from '@/components/GbButton.vue'
import GbInlineMessage from '@/components/GbInlineMessage.vue'
import IODGuardrail from './IODGuardrail.vue'
import SaveIODConfirmationModal from './SaveIODConfirmationModal.vue'
import PremiumActionModal from '@/components/PremiumActionModal.vue'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import { useUserStore } from '@/authentication/user.store'
import { ACTION_TYPES, CONTENT_TYPES, actionPermissions } from '@/util/access_visibility'
import { useNonAIMode } from './composables/useNonAIMode'
import { useSaveIODConfirmation } from './composables/useSaveIODConfirmation'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/dialog'
import { useLibraryStore } from '@/library/library.store'
import { useImpactOfDisabilityStore } from './impact-of-disability.store'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { transformWikiToResourceComponents, transformResourceComponentsToWiki } from '@/components/editor/wiki_tiptap_helpers'
import { announce, clearAnnouncement } from '@/util/live_announcer'

interface Props {
  disability: string
  disabilityLabel: string
  subjects: string[]
  impactAreas: ImpactArea[]
  impactAreaOptions: ImpactArea[]
  changeRequiresConfirmation?: boolean
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  changeRequiresConfirmation: false,
  loading: false
})

const emit = defineEmits<{
  'update:impactAreas': [value: ImpactArea[]]
  'update:changeRequiresConfirmation': [value: boolean]
  'confirmupdate:impactAreas': [value: ImpactArea[]]
}>()

const router = useRouter()
const route = useRoute()
const analytics = getAnalyticsTracker()
const libraryStore = useLibraryStore()
const iodStore = useImpactOfDisabilityStore()
const contentProfileStore = useContentProfileStore()
const visibilityStore = useVisibilityStore()
const contentReviewStore = useContentReviewStore()
const isNonAIMode = useNonAIMode()
const { confirmSave } = useSaveIODConfirmation()

const { isReviewing, reviewResults, reviewError, hasResults, startReview, cancelReview, clearResults } =
  useContentReview()

// A demo can change the review profile while the account profile stays put, so results scored
// against the old rubric would sit under criteria for the new one. Abort any run in flight and
// clear what is showing; the presenter reviews again under the rubric they just chose.
watch(() => contentReviewStore.reviewProfileId, () => {
  cancelReview()
  clearResults()
})
const showReviewModal = ref(false)

const impactCriteria = computed(() =>
  contentReviewStore.impactCriteriaForProfile(
    contentReviewStore.reviewProfileId,
    visibilityStore.betaVisibility
  )
)
const showReviewButton = computed(() =>
  !isNonAIMode.value &&
  impactCriteria.value.length > 0 &&
  !isStatementEmptyPlaceholder.value
)
// Derived from whether the "Select Impact Areas" heading is visible so the accessible name
// never claims a picker that isn't rendered (WCAG 2.5.3).
const editorAriaLabel = computed(() =>
  props.impactAreaOptions.length > 1
    ? 'Select Impact Areas Content text editor'
    : `${props.disabilityLabel} impact statement Content text editor`
)
const { reviewCriteriaSetName, criteriaEventAttrs } = useImpactCriteriaAttrs()

const {
  handleAutoReviewButtonClick: _handleAutoReviewButtonClick,
  handleModalReview,
  handleCloseReviewResults: _handleCloseReviewResults,
  handleReviewResultsToggle,
  handleReceivedReviewResults,
} = useImpactStatementReviewAnalytics(analytics, criteriaEventAttrs, hasResults)

function handleAutoReviewButtonClick() {
  _handleAutoReviewButtonClick(() => { showReviewModal.value = true })
}

function handleCloseReviewResults() {
  _handleCloseReviewResults(() => { clearResults() })
}

function handleReview() {
  handleModalReview(() => {
    showReviewModal.value = false
    startReview(
      ({ signal, onProgress }) =>
        reviewImpactStatementStream({
          impactStatementText: statement.value,
          contentProfile: contentReviewStore.reviewProfileId,
          subjects: props.subjects,
          betaMode: visibilityStore.betaVisibility,
          signal,
          onProgress,
        }),
      handleReceivedReviewResults
    )
  })
}

const toEmbeddedText = transformWikiToResourceComponents
const toWikiText = transformResourceComponentsToWiki

const selectedImpactAreas = ref<ImpactArea[]>([...props.impactAreas])
const impactAreasToCompose = computed(() => composedImpactAreas(props.impactAreas, props.impactAreaOptions))
const statement = ref('')
const unEditedStatement = ref('')
const isStatementEmptyPlaceholder = ref(false)
const isLoadingStatement = ref(false)
const showSaveToFolderModal = ref(false)
const savedFolderError = ref(false)
const statementGenerationError = ref(false)
const showConfirmDialog = ref(false)
const pendingImpactAreas = ref<ImpactArea[]>([])
const templateStatementsByDisability = new Map<string, StatementForDisability[]>()

const isGeneratingStatement = computed(() => props.loading || isLoadingStatement.value)
const STATEMENT_UPDATED_MESSAGE = 'Impact statement updated'
const loadingStatementMessage = computed(() =>
  isNonAIMode.value ? 'Loading impact statement...' : 'Generating impact statement...'
)
watch(isGeneratingStatement, generating => {
  if (generating) {
    announce(loadingStatementMessage.value)
  }
})

const isGuardrailShown = computed(() =>
  !isNonAIMode.value &&
  visibilityStore.iodGuardrailVisibility &&
  Boolean(props.disabilityLabel) &&
  !isStatementEmptyPlaceholder.value
)
const saveConfirmation = computed(() => (isNonAIMode.value ? confirmSave : undefined))
// Both dialogs share the overlay z-index, so the one teleported to <body> last is on top. Mounting
// the confirmation after the save modal opens puts it there; mounted earlier, it opens behind.
const isSaveConfirmationMounted = computed(() => isNonAIMode.value && showSaveToFolderModal.value)

// Vue 2 gated and counted the non-AI statement's copies; the AI mode has never tracked them.
const statementCopyWrapper = computed(() => (isNonAIMode.value ? CopyTrackWrapper : 'div'))
const statementCopyWrapperProps = computed(() => (isNonAIMode.value ? { type: 'impact_of_disability', gateCopy: true } : {}))

const userStore = useUserStore()
const savePermissions = computed(() =>
  actionPermissions(ACTION_TYPES.SAVE, CONTENT_TYPES.IMPACT_OF_DISABILITY, {}, userStore.authInfo)
)
// Only the non-AI mode stops a trial or expired user's save; the AI mode has never checked.
const isSaveBlocked = computed(() => isNonAIMode.value && savePermissions.value.block)
const isSaveBlockedModalOpen = ref(false)

watch([() => props.disability, () => props.subjects, () => props.impactAreas, () => contentProfileStore.profileId], async ([disability, subjects, impactAreas, profileId], [oldDisability, oldSubjects, oldImpactAreas, oldProfileId]) => {
  // Clear cache when disability or content profile changes to ensure fresh statements
  if (disability !== oldDisability || profileId !== oldProfileId) {
    iodStore.clearCache()
    clearResults()
  }
  
  selectedImpactAreas.value = [...impactAreas]
  await pieceStatementsTogether()
  updateRoute()
}, {deep: true, immediate: true})

onMounted(async () => {
  analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.VIEWED_RESULTS)
  if (!isNonAIMode.value && !contentReviewStore.profiles.length) {
    contentReviewStore.fetchProfiles()
  }
})

async function getStatements() {
  statementGenerationError.value = false
  try {
    if (props.subjects.length < 1 || props.disability.length < 1) {
      return []
    }

    // If disability has multiple impact areas (>1) but none are selected, don't make API call
    // Disabilities with only 1 impact area should still generate statements
    if (props.impactAreaOptions.length > 1 && props.impactAreas.length < 1) {
      return []
    }

    const impactAreaLabels = impactAreasToCompose.value.map(ia => ia.area_label)
    
    // Get cached statements
    const cachedStatements = iodStore.getCachedStatements(props.disabilityLabel, props.subjects, impactAreaLabels, contentProfileStore.profileId)
    
    // Check if all requested combinations are already cached
    if (iodStore.areAllStatementsCached(props.disabilityLabel, props.subjects, impactAreaLabels, contentProfileStore.profileId)) {
      return transformStatements(cachedStatements)
    }

    // Identify missing combinations that need to be generated
    const missing = iodStore.getMissingCombinations(props.disabilityLabel, props.subjects, impactAreaLabels, contentProfileStore.profileId)
    
    if (missing.subjects.length === 0 || missing.impactAreas.length === 0) {
      return transformStatements(cachedStatements)
    }

    // Generate only the missing statements via API
    const response = await generateIODStatements({
      disability: props.disabilityLabel,
      subjects: missing.subjects,
      impact_areas: missing.impactAreas,
      content_profile: contentProfileStore.profileId,
      beta_only: visibilityStore.betaVisibility
    })

    // Cache the newly generated statements (including custom prompt flag)
    iodStore.cacheGeneratedStatements(props.disabilityLabel, response.statements, contentProfileStore.profileId, response.is_custom_prompt)

    // Combine cached and newly generated statements
    const allStatements = [...cachedStatements, ...response.statements]
    return transformStatements(allStatements)
  } catch (error) {
    console.error('Error generating IOD statements:', error)
    statementGenerationError.value = true
    return []
  }
}

function transformStatements(statements: any[]): StatementForDisability[] {
  return statements
    .map((stmt): StatementForDisability | null => {
      const matchingImpactArea = props.impactAreaOptions.find(
        (ia) => ia.area_label === stmt.impact_area
      )

      if (!matchingImpactArea) {
        console.warn('No matching impact area for label:', stmt.impact_area)
        return null
      }

      return {
        subject: stmt.subject,
        area_uuid: matchingImpactArea.area_uuid,
        statement_text: stmt.statement_text
      }
    })
    .filter((stmt): stmt is StatementForDisability => stmt !== null)
}

async function composeGeneratedStatement(): Promise<[string, boolean]> {
  const statementsForDisability = await getStatements()


  return buildIODStatement({
    statementsForDisability,
    selectedSubjects: props.subjects,
    selectedImpactAreas: impactAreasToCompose.value
  })
}

async function getTemplateStatements(): Promise<StatementForDisability[]> {
  statementGenerationError.value = false
  const cachedStatements = templateStatementsByDisability.get(props.disability)
  if (cachedStatements) {
    return cachedStatements
  }

  try {
    const { statements } = await getTemplateIODStatements(props.disability, contentProfileStore.profileId)
    templateStatementsByDisability.set(props.disability, statements)
    return statements
  } catch (error) {
    console.error('Error loading template IOD statements:', error)
    statementGenerationError.value = true
    return []
  }
}

async function composeTemplateStatement(): Promise<[string, boolean]> {
  return buildTemplateIODStatement({
    impactAreaOptions: props.impactAreaOptions,
    statementsForDisability: await getTemplateStatements(),
    selectedSubjects: props.subjects,
    selectedImpactAreas: props.impactAreas
  })
}

async function pieceStatementsTogether() {
  isLoadingStatement.value = true
  try {
    const [composedStatement, isComposedStatementPlaceholder] = isNonAIMode.value
      ? await composeTemplateStatement()
      : await composeGeneratedStatement()

    statement.value = composedStatement
    isStatementEmptyPlaceholder.value = isComposedStatementPlaceholder
    emit('update:changeRequiresConfirmation', false)
    unEditedStatement.value = statement.value
    if (statementGenerationError.value) {
      // The error message is role="alert" and announces itself; a pending "Generating..." would land after it.
      clearAnnouncement()
    } else if (!isComposedStatementPlaceholder) {
      announce(STATEMENT_UPDATED_MESSAGE)
    }
  } finally {
    isLoadingStatement.value = false
  }
}

function updateRoute() {
  const newQuery = {
    ...route.query,
    subjects: props.subjects,
    disability: props.disability,
    impactAreas: props.impactAreas.map(ia => ia.area_uuid)
  }
  
  router.push({
    name: 'ImpactOfDisability',
    query: newQuery
  })
}

function handleImpactAreaClick(newVal: ImpactArea[]) {
  if (props.changeRequiresConfirmation) {
    pendingImpactAreas.value = newVal
    showConfirmDialog.value = true
  } else {
    selectedImpactAreas.value = newVal
    emit('update:impactAreas', newVal)
  }
}

function confirmImpactAreaChange() {
  selectedImpactAreas.value = pendingImpactAreas.value
  emit('update:impactAreas', pendingImpactAreas.value)
  emit('update:changeRequiresConfirmation', false)
  analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.RESET_STATEMENT_TEXT)
  showConfirmDialog.value = false
}

function cancelImpactAreaChange() {
  analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.DISMISSED_STATEMENT_RESET)
  showConfirmDialog.value = false
}

function handleEditModeUpdated(isEditMode: boolean) {
  if (!isEditMode) {
    // Strip HTML tags and whitespace for comparison
    const stripHtml = (str: string) => str.replace(/<\/?p>/g, '').trim().replace(/(\s)/gm, '')
    const currentStatement = stripHtml(statement.value)
    const originalStatement = stripHtml(unEditedStatement.value)
    
    if (currentStatement !== originalStatement) {
      emit('update:changeRequiresConfirmation', true)
      analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.EDITED_STATEMENT, { edited_text: statement.value })
    } else {
      emit('update:changeRequiresConfirmation', false)
    }
  }
}

// The saved and shared statement pages read area_label from the non-AI mode's impact area
// records; the AI mode has always saved bare labels.
const savedImpactAreas = computed(() =>
  isNonAIMode.value ? impactAreasToCompose.value : props.impactAreas.map(ia => ia.area_label)
)

const statementPayload = computed(() => ({
  disability: props.disabilityLabel,
  subjects: props.subjects,
  impact_areas: savedImpactAreas.value,
  statement: statement.value
}))

let saveModalOpener: HTMLElement | null = null

// Safari doesn't focus a button on click, so the clicked button is the opener, not the active element.
function openSaveModal(event: MouseEvent) {
  if (isSaveBlocked.value) {
    isSaveBlockedModalOpen.value = true
    return
  }
  saveModalOpener = event.currentTarget as HTMLElement | null
  showSaveToFolderModal.value = true
}

// Declining the save warning unmounts the save modal before PrimeVue can hand focus back.
async function saveToFolderModalVisibilityChanged(isVisible: boolean) {
  showSaveToFolderModal.value = isVisible
  if (isVisible || !isNonAIMode.value) {
    return
  }
  await nextTick()
  saveModalOpener?.focus()
}

// Each mode keeps the saved-statement payload its users' analytics already record: none for AI,
// the folder and selection fields for non-AI.
function sendSavedStatementEvent(folder: { uuid: string }) {
  if (!isNonAIMode.value) {
    analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.SAVED_STATEMENT)
    return
  }
  analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.SAVED_STATEMENT, {
    user_folder_role: libraryStore.getUserRoleForFolder(folder.uuid),
    iod_page: 'Compose Impact of Disability',
    iod_selected_disability: props.disabilityLabel,
    iod_selected_impact_areas: impactAreasToCompose.value.map(impactArea => impactArea.area_label),
    iod_selected_subjects: props.subjects
  })
}

async function handleSaveToFolder(folder: { uuid: string, name: string }) {
  savedFolderError.value = false
  sendSavedStatementEvent(folder)
  try {
    await libraryStore.fetchFoldersForContentByType('saved_impact_of_disability')
  } catch (error) {
    savedFolderError.value = true
    console.error('Failed to refresh library state after save:', error)
  }
}
</script>

